import { useEffect, useId, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { RateView } from '@/types/product';
import { groupRates, rateGroup } from './rateGroups';
import { money, rateName } from './workbenchPricing';

interface Props {
  label: string;
  value: string;
  rates: RateView[];
  ratesByKey: Record<string, number>;
  disabled?: boolean;
  onChange: (key: string) => void;
}

export default function RateCombobox({
  label,
  value,
  rates,
  ratesByKey,
  disabled,
  onChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const selected = rates.find((rate) => rate.key === value);
  const search = query.trim().toLocaleLowerCase('nl');
  const groups = groupRates(
    rates.filter((rate) =>
      `${rateGroup(rate)} ${rate.name} ${money(ratesByKey[rate.key])}`
        .toLocaleLowerCase('nl')
        .includes(search)
    )
  );
  const options = groups.flatMap((group) => group.rates);
  const optionId = (id: string) => `${listId}-${id}`;
  useEffect(() => {
    if (open && options[active])
      document
        .getElementById(`${listId}-${options[active].id}`)
        ?.scrollIntoView({ block: 'nearest' });
  }, [open, active, listId, options]);
  const choose = (key: string) => {
    onChange(key);
    setOpen(false);
  };
  return (
    <Popover
      modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setQuery('');
          setActive(
            Math.max(
              0,
              groupRates(rates)
                .flatMap((g) => g.rates)
                .findIndex((r) => r.key === value)
            )
          );
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={label}
          aria-haspopup="listbox"
          title={
            selected
              ? `${selected.name} · ${money(ratesByKey[value])} / uur`
              : value
                ? 'Onbekend tarief'
                : 'Kies een tarief'
          }
          className="flex h-8 min-w-0 items-center justify-end gap-1 rounded border border-transparent px-1 text-xs hover:border-input focus-visible:border-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        >
          <span className="truncate">
            {selected
              ? `${money(ratesByKey[value])} · ${rateName(selected)}`
              : value
                ? 'Onbekend tarief'
                : 'Kies een tarief'}
          </span>
          <ChevronDown className="h-3 w-3 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="z-[210] w-[360px] max-w-[calc(100vw-2rem)] p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          searchRef.current?.focus();
        }}
      >
        <div className="relative border-b p-2">
          <Search className="pointer-events-none absolute left-4 top-4 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchRef}
            role="combobox"
            aria-label="Uurtarief zoeken"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={options[active] ? optionId(options[active].id) : undefined}
            placeholder="Zoek werkzaamheid, groep of bedrag"
            className="pl-8"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                if (options.length)
                  setActive(
                    (current) =>
                      (current + (event.key === 'ArrowDown' ? 1 : -1) + options.length) %
                      options.length
                  );
              } else if (event.key === 'Enter') {
                event.preventDefault();
                if (options[active]) choose(options[active].key);
              }
            }}
          />
        </div>
        <div
          id={listId}
          role="listbox"
          aria-label="Uurtarieven"
          className="max-h-[min(360px,50vh)] overflow-y-auto overscroll-contain p-1"
        >
          {groups.map((group) => (
            <div key={group.name} role="group" aria-label={group.name}>
              <div className="px-2 pb-1 pt-3 text-xs font-semibold text-muted-foreground">
                {group.name}
              </div>
              {group.rates.map((rate) => (
                <button
                  key={rate.id}
                  id={optionId(rate.id)}
                  type="button"
                  role="option"
                  aria-selected={rate.key === value}
                  tabIndex={-1}
                  className={`flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm ${options[active]?.id === rate.id ? 'bg-secondary' : 'hover:bg-muted'}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(rate.key)}
                >
                  <Check className={`h-4 w-4 shrink-0 ${rate.key === value ? '' : 'invisible'}`} />
                  <span className="min-w-0 flex-1 break-words">{rateName(rate)}</span>
                  <span className="shrink-0 tabular-nums">{money(ratesByKey[rate.key])}</span>
                </button>
              ))}
            </div>
          ))}
          {!options.length && (
            <p role="status" className="px-3 py-5 text-sm text-muted-foreground">
              Geen tarieven gevonden. Probeer een andere zoekterm.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
